import React, { useState, useEffect, useRef } from 'react';
import { 
  IonContent, 
  IonHeader, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButtons, 
  IonBackButton,
  IonList,
  IonItem,
  IonLabel,
  IonFab,
  IonFabButton,
  IonIcon,
  IonSearchbar,
  IonText,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonListHeader,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
  IonBadge
} from '@ionic/react';
import { add, documentTextOutline, micOutline, timeOutline, alertCircleOutline, people, personOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import getApi from '../api';

export type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'none';

export interface User {
  id: number;
  email: string;
  name: string;
  company_name?: string;
}

export interface Note {
  id: string;
  title: string;
  content: string;
  audio_url?: string;
  transcription_status: TranscriptionStatus;
  created_at: string;
  updated_at: string;
  owner?: User;
  shared_with?: User[];
  is_owner?: boolean;
}

const Notes: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'own' | 'shared_with_me'>('all');
  const history = useHistory();
  const api = getApi();
  const intervalRef = useRef<any>(null);
  const filterRef = useRef(filter);

  // Keep filterRef in sync with filter state
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  const fetchNotes = async (silent = false, filterToUse?: string) => {
    if (!silent) setLoading(true);
    try {
      const currentFilter = filterToUse || filterRef.current;
      const filterParam = currentFilter !== 'all' ? `?filter=${currentFilter}` : '';
      const response = await api.get(`/api/user/notes/${filterParam}`);
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setNotes(data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [filter]);

  useIonViewWillEnter(() => {
    fetchNotes();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      fetchNotes(true);
    }, 5000);
  });

  useIonViewWillLeave(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  });

  useEffect(() => {
    let sorted = [...notes].sort((a: Note, b: Note) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    if (searchText.trim() !== '') {
      const lowerText = searchText.toLowerCase();
      sorted = sorted.filter((note: Note) => 
        note.title.toLowerCase().includes(lowerText) || 
        (note.content && note.content.toLowerCase().includes(lowerText))
      );
    }
    setFilteredNotes(sorted);
  }, [searchText, notes]);

  const handleNoteClick = (id: string) => {
    history.push(`/notes/${id}`);
  };

  const handleAddNote = () => {
    history.push('/notes/new');
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getGroupLabel = (isoString: string) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Idag';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Igår';
    }
    
    const dateStr = date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
    return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  };

  // Group notes
  const groupedNotes: { label: string, notes: Note[] }[] = [];
  filteredNotes.forEach(note => {
    const label = getGroupLabel(note.created_at);
    const lastGroup = groupedNotes[groupedNotes.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.notes.push(note);
    } else {
      groupedNotes.push({ label, notes: [note] });
    }
  });

  const getStatusIcon = (status: TranscriptionStatus) => {
    switch (status) {
        case 'pending':
        case 'processing':
            return timeOutline;
        case 'failed':
            return alertCircleOutline;
        case 'completed':
            return micOutline;
        default:
            return documentTextOutline;
    }
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/Home" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Anteckningar</IonTitle>
        </IonToolbar>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonSearchbar 
            value={searchText} 
            onIonChange={e => setSearchText(e.detail.value!)} 
            placeholder="Sök anteckningar"
            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
          />
        </IonToolbar>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonSegment value={filter} onIonChange={e => setFilter(e.detail.value as any)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonSegmentButton value="all" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Alla</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="own" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Mina</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="shared_with_me" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Delade med mig</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        {loading ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </div>
        ) : filteredNotes.length === 0 ? (
          <div style={{ textAlign: 'center', marginTop: '50px', padding: '20px' }}>
            <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <p>Inga anteckningar hittades.</p>
            </IonText>
          </div>
        ) : (
          <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {groupedNotes.map(group => (
              <React.Fragment key={group.label}>
                <IonListHeader style={{ minHeight: 'auto', paddingTop: '10px', paddingBottom: '5px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel style={{ fontSize: '0.9em', fontWeight: 'bold', color: 'var(--ion-color-medium)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {group.label}
                  </IonLabel>
                </IonListHeader>
                {group.notes.map(note => (
                  <IonItem key={note.id} button onClick={() => handleNoteClick(note.id)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonIcon slot="start" icon={getStatusIcon(note.transcription_status)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <h2>
                        {note.title || 'Namnlös anteckning'}
                        {!note.is_owner && note.owner && (
                          <IonBadge color="medium" style={{ marginLeft: '8px', fontSize: '0.7em' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonIcon icon={personOutline} style={{ marginRight: '2px', fontSize: '0.9em' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            {note.owner.name}
                          </IonBadge>
                        )}
                        {note.is_owner && note.shared_with && note.shared_with.length > 0 && (
                          <IonBadge color="primary" style={{ marginLeft: '8px', fontSize: '0.7em' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonIcon icon={people} style={{ marginRight: '2px', fontSize: '0.9em' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            {note.shared_with.length}
                          </IonBadge>
                        )}
                      </h2>
                      <p>
                        <span style={{ marginRight: '8px' }}>{formatTime(note.created_at)}</span>
                        {note.content && <span style={{ color: '#666' }}>{note.content.substring(0, 40)}{note.content.length > 40 ? '...' : ''}</span>}
                        {!note.content && note.transcription_status === 'processing' && <span style={{ color: '#666', fontStyle: 'italic' }}>Transkriberar...</span>}
                      </p>
                    </IonLabel>
                    {note.transcription_status === 'processing' && <IonSpinner name="dots" style={{ width: '20px', height: '20px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
                  </IonItem>
                ))}
              </React.Fragment>
            ))}
          </IonList>
        )}

        <IonFab vertical="bottom" horizontal="end" slot="fixed" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonFabButton onClick={handleAddNote} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonIcon icon={add} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
          </IonFabButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

export default Notes;
